<template>
  <div class="app-container">
    <AppHeader
      :hisseler="hisseler"
      :selectedHisse="selectedHisse"
      @select-hisse="selectHisse"
    />

    <div class="main-content">
      <div class="background-icon"></div>

      <p v-if="error" class="error-message">{{ error }}</p>

      <div v-if="!selectedHisse" class="welcome-screen">
        <div class="welcome-icon"></div>
        <h1>Temel Analiz Raporu</h1>
        <p>Detaylı bilgi görmek için yukarıdan bir hisse arayın ve seçin.</p>
      </div>

      <div v-if="selectedHisse" class="hisse-detay-container">
        <HisseHeader
          :selectedHisse="selectedHisse"
          :temelAnalizDegerleri="temelAnalizDegerleri"
        />

        <FiyatGrafik :tarihselFiyatBilgileri="tarihselFiyatBilgileri" />

        <TemelAnaliz :temelAnalizDegerleri="temelAnalizDegerleri" />

        <FinansalVeriler :ozetRapor="ozetRapor" />

        <div class="card">
          <h3>Sermaye Artırımları Ve Temettüler</h3>
          <TemettulerGrafik :temettuler="temettuler" />
          <SermayeArtirimlariGrafik :sermayeArtirimlar="sermayeArtirimlari" />
        </div>

        <SirketBilgileri :sirketBilgileri="sirketBilgileri" />
      </div>

      <LoadingProgress :loading="loading" :loadingStates="loadingStates" />
    </div>
  </div>
</template>

<script>
import AppHeader from "../components/AppHeader.vue";
import HisseHeader from "../components/HisseHeader.vue";
import FiyatGrafik from "../components/FiyatGrafik.vue";
import TemelAnaliz from "../components/TemelAnaliz.vue";
import FinansalVeriler from "../components/FinansalVeriler.vue";
import TemettulerGrafik from "../components/TemettulerGrafik.vue";
import SermayeArtirimlariGrafik from "../components/SermayeArtirimlariGrafik.vue";
import SirketBilgileri from "../components/SirketBilgileri.vue";
import LoadingProgress from "../components/LoadingProgress.vue";

import { getHisseListesi, getHisseData } from "@/services/hisseService";
import { generateOzetRapor } from "@/utils/ozetRapor";
import { generateTemelAnalizDegerleri } from "@/utils/temelAnalizDegerleri";

export default {
  components: {
    AppHeader,
    HisseHeader,
    FiyatGrafik,
    TemelAnaliz,
    FinansalVeriler,
    TemettulerGrafik,
    SermayeArtirimlariGrafik,
    SirketBilgileri,
    LoadingProgress,
  },
  data() {
    return {
      hisseler: [],
      selectedHisse: null,
      error: null,
      tarihselFiyatBilgileri: [],
      ozetRapor: [],
      temelAnalizDegerleri: [],
      temettuler: [],
      sermayeArtirimlari: [],
      sirketBilgileri: [],
      loading: false,
      loadingStates: {
        bilanco: false,
        gelirTablosu: false,
        sermayeArtirimlari: false,
        tarihselFiyat: false,
        sirketBilgileri: false,
      },
    };
  },
  async created() {
    try {
      const hisseListesi = await getHisseListesi();
      this.hisseler = hisseListesi.filter((hisse) => {
        const hisseKoduLength = hisse.hisse_kodu.length;
        return hisseKoduLength === 4 || hisseKoduLength === 5;
      });
    } catch (error) {
      this.error = error.message;
    }
  },
  methods: {
    async selectHisse(hisse) {
      this.loading = true;

      this.selectedHisse = null;
      this.tarihselFiyatBilgileri = [];
      this.ozetRapor = [];
      this.temelAnalizDegerleri = [];
      this.temettuler = [];
      this.sermayeArtirimlari = [];
      this.sirketBilgileri = [];
      this.error = null;

      // Tüm loading durumlarını sıfırla
      this.loadingStates = {
        bilanco: false,
        gelirTablosu: false,
        sermayeArtirimlari: false,
        tarihselFiyat: false,
        sirketBilgileri: false,
      };

      this.selectedHisse = hisse;

      this.$nextTick(() => {
        const mainContent = document.querySelector(".main-content");
        if (mainContent) {
          mainContent.scrollTop = 0;
        }
      });

      try {
        // updateLoadingState fonksiyonu oluştur - bunu hisseService'e geçireceğiz
        const updateLoadingState = (key, value, isError = false) => {
          if (Object.prototype.hasOwnProperty.call(this.loadingStates, key)) {
            this.loadingStates[key] = value;

            if (isError) {
              console.error(`${key} verisi yüklenirken hata oluştu`);
            }
          }
        };

        const hisseData = await getHisseData(hisse, updateLoadingState);

        this.temettuler = hisseData.sermayeArtirimlariTemettuler.temettuler;
        this.sermayeArtirimlari =
          hisseData.sermayeArtirimlariTemettuler.sermaye_artirimlari;
        this.tarihselFiyatBilgileri = hisseData.tarihselFiyatBilgileri;
        this.sirketBilgileri = hisseData.sirketBilgileri;

        this.ozetRapor = generateOzetRapor(
          hisseData.bilancoVerileri,
          hisseData.gelirTablosuVerileri,
          hisseData.sermayeArtirimlariTemettuler,
          hisseData.tarihselFiyatBilgileri,
          hisseData.sirketBilgileri
        );

        if (!this.ozetRapor) {
          throw new Error("Özet rapor oluşturulamadı: Eksik veri");
        }

        this.temelAnalizDegerleri = generateTemelAnalizDegerleri(
          this.ozetRapor
        );
      } catch (error) {
        this.error = error.message;
      } finally {
        // Tüm yükleme işlemleri tamamlandı
        this.loading = false;
      }
    },
  },
};
</script>

<style>
:root {
  --primary-color: #2563eb;
  --primary-light: #3b82f6;
  --primary-dark: #1d4ed8;
  --success-color: #10b981;
  --danger-color: #ef4444;
  --bg-color: #0f172a;
  --card-bg: #1e293b;
  --card-dark: #0f172a;
  --border-color: #334155;
  --text-primary: #f8fafc;
  --text-secondary: #94a3b8;
  --text-muted: #64748b;
  --hover-bg: #334155;
}

* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    sans-serif;
}

body,
html,
#app {
  margin: 0;
  padding: 0;
  height: 100%;
  width: 100%;
  background-color: var(--bg-color);
  color: var(--text-primary);
  overflow: hidden;
}

.app-container {
  display: flex;
  flex-direction: column;
  height: 100vh;
  width: 100%;
  overflow: hidden;
}

.main-content {
  flex: 1;
  overflow-y: auto;
  padding: 1.5rem;
  background-color: var(--bg-color);
  position: relative;
}

.background-icon {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 720px;
  height: 720px;
  background-image: url("@/assets/logo.png");
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  opacity: 0.025;
  pointer-events: none;
  z-index: 0;
}

.welcome-screen {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 80%;
  color: var(--text-secondary);
  gap: 1rem;
  position: relative;
  z-index: 1;
  text-align: center;
}

.welcome-icon {
  color: var(--primary-light);
  opacity: 0.7;
}

.hisse-detay-container {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding-bottom: 4rem;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
}

.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(15, 23, 42, 0.8);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 100;
}

.spinner {
  border: 3px solid rgba(59, 130, 246, 0.3);
  border-radius: 50%;
  border-top: 3px solid var(--primary-light);
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.error-message {
  margin-top: 1rem;
  padding: 0.75rem;
  background-color: rgba(239, 68, 68, 0.1);
  border: 1px solid var(--danger-color);
  border-radius: 8px;
  color: var(--danger-color);
  font-size: 14px;
  max-width: 1200px;
  margin: 1rem auto;
  position: relative;
  z-index: 1;
}

.card {
  background-color: var(--card-bg);
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);
  position: relative;
  z-index: 1;
  width: 100%;
}

.card h3 {
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 1rem;
}

.chart-card {
  padding: 1.5rem;
  background-color: var(--card-dark);
  border-radius: 8px;
  border: 1px solid var(--border-color);
  display: flex;
  flex-direction: column;
  gap: 1rem;
  height: auto;
  overflow-x: auto;
}

.chart-card h3 {
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: -0.75rem;
}

::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-track {
  background-color: var(--bg-color);
  border-radius: 6px;
}

::-webkit-scrollbar-thumb {
  background-color: var(--hover-bg);
  border-radius: 6px;
  border: 2px solid var(--bg-color);
}

::-webkit-scrollbar-thumb:hover {
  background-color: var(--primary-light);
}

::-webkit-scrollbar-thumb:active {
  background-color: var(--primary-dark);
}

@media (max-width: 768px) {
  .background-icon {
    width: 500px;
    height: 500px;
  }

  .main-content {
    padding: 1rem;
  }

  .card {
    padding: 1rem;
  }

  .chart-card {
    padding: 1rem;
  }

  .hisse-detay-container {
    gap: 1rem;
    padding-bottom: 2rem;
  }
}

@media (max-width: 480px) {
  .background-icon {
    width: 400px;
    height: 400px;
  }

  .main-content {
    padding: 0.75rem;
  }

  .card {
    padding: 0.75rem;
    border-radius: 8px;
  }

  .chart-card {
    padding: 0.75rem;
  }

  .card h3 {
    font-size: 16px;
  }

  .chart-card h3 {
    font-size: 14px;
  }

  .welcome-screen h2 {
    font-size: 1.25rem;
  }

  .welcome-screen p {
    font-size: 0.875rem;
  }
}

@media (max-width: 360px) {
  .background-icon {
    width: 300px;
    height: 300px;
  }

  .main-content {
    padding: 0.5rem;
  }

  .card {
    padding: 0.75rem;
    margin-bottom: 1rem;
  }
}

.table-responsive {
  overflow-x: auto;
  width: 100%;
  margin-bottom: 1rem;
}

table {
  width: 100%;
  border-collapse: collapse;
}

table th,
table td {
  padding: 0.5rem;
  text-align: left;
  font-size: 0.875rem;
}

@media (max-width: 768px) {
  table th,
  table td {
    padding: 0.35rem;
    font-size: 0.75rem;
  }
}
</style>
